/**
 * <process-tabs> — folds the process page's sections into tabs.
 *
 * The page is written as every section in order, so without this script it reads straight through,
 * headings and all. That is the accessible path the ticket asks for, and it is the page's actual
 * structure rather than a fallback bolted on.
 *
 * The tabs are built here rather than in Liquid because a theme block reports its type as @theme
 * to its section and hands over no settings — the section cannot read its own sections' names.
 * Each panel carries its own name, which is the one place that name is known.
 *
 * On a wide screen the sections become a tab panel each, following the tabs pattern: one tab in
 * the tab order, arrow keys and Home and End moving between them, and the panel labelled by its
 * tab. The halves of the page become previous and next, as live has them, but as real buttons
 * that can be reached and named rather than bare click handlers.
 *
 * On a phone live does not fold at all — every section is stacked and the row above them jumps
 * down to each one — so neither do we, and the tabs go back to being ordinary links. The
 * alternative, folding on a phone too, is what this was built as first; if UX review prefers it,
 * it is this method and the breakpoint above, nothing else.
 *
 * Folded, the page also opens immersed, as the lookbook does (SAVETTE-70: "just like the Lookbook
 * template pages do"), unless the merchant has turned off Hide the header when the page opens
 * (the section then leaves out data-immersion-src). 800ms after it mounts the page eases down to
 * the top of this element, so the header and banner scroll off and the tab row and its section
 * fill the screen; and every use of them — a tab or a flip half clicked, a click anywhere in the
 * section, the tab row's arrow keys and Home and End — eases it back there if the shopper has
 * scrolled the page since to see the header or the footer. Scrolling itself never moves the
 * page: the shopper went there on purpose, and neither does selecting copy, a modified click or
 * a link in the copy, which are the shopper reading or leaving rather than using the section.
 * The section is at least a screen tall once folded (the panel's band is the screen less the tab
 * row), so at that position the footer is below the fold too.
 *
 * The delay, the guards and the easing are the lookbook's, shared through immersion.js: nothing
 * moves on a phone, a tablet or below 800px, or in the theme editor, nor for a shopper who prefers
 * reduced motion, has already scrolled or moved focus. On top of that, nothing moves while the
 * page is stacked (below 1025px): it is one long page there, read by scrolling, and its tab row
 * sticks under the header's height, so there is no single place that is "the section filling
 * the screen" to return to. Nor while the header is set to stay on screen (Keep header visible
 * while scrolling): it cannot scroll off, and easing to this element's top would tuck the tab
 * row under it. The lookbook avoids that by unpinning its header; the process pages do not.
 *
 * immersion.js is imported from the versioned URL the section puts on this element, as the
 * lookbook does (loadImmersion; why there is no static import is in immersion.js).
 */
class ProcessTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[data-process-tablist]');
    this.panels = [...this.querySelectorAll('[data-process-panel]')];
    this.prev = this.querySelector('[data-process-prev]');
    this.next = this.querySelector('[data-process-next]');
    if (!this.tablist || this.panels.length === 0) return;

    this.tabs = this.panels.map((panel) => this.buildTab(panel));
    this.tablist.hidden = false;

    // Live only folds the page on desktop. On a phone every section is stacked and the row above
    // them jumps down to each one, so the same markup is read two ways.
    this.wide = window.matchMedia('(min-width: 1025px)');
    this.wide.addEventListener('change', this.onWideChange);

    // marks the page as folded, which is what reveals the flip halves
    this.dataset.scripted = '';

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', (event) => {
        // stacked, the tab is a plain link and the browser's jump is the right behaviour
        if (!this.wide.matches) return;
        event.preventDefault();
        this.select(i, { focusTab: true });
      });
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, i));
    });
    this.prev?.addEventListener('click', () => this.select(this.index - 1));
    this.next?.addEventListener('click', () => this.select(this.index + 1));

    this.apply();

    // a click in the section is a use of it (onUse); it bubbles here after the tabs' and the flip
    // halves' own listeners, so the new section is already showing
    this.addEventListener('click', this.onUse);
    this.loadImmersion();
  }

  disconnectedCallback() {
    this.wide?.removeEventListener('change', this.onWideChange);
    this.removeEventListener('click', this.onUse);
    this.immersionLoad = null;
    this.immersion?.disconnect();
    this.immersion = null;
  }

  /**
   * immersion.js from the URL on this element (data-immersion-src), only there while the
   * merchant's setting is on. The arrival counts from this moment, not from the import: connect()
   * is handed the time and scroll position noted now. Until it resolves a use simply does not
   * re-align, and if the section leaves the page first (the theme editor re-rendering it),
   * nothing is attached.
   */
  loadImmersion() {
    const src = this.dataset.immersionSrc;
    if (!src) return;
    const arrival = { at: performance.now(), scrollY: window.scrollY };
    const load = (this.immersionLoad = {});
    // only a failed import is swallowed (the page then simply stays put); anything connect()
    // throws is a bug and surfaces
    import(src).then(
      ({ Immersion }) => {
        if (this.immersionLoad !== load || !this.isConnected) return;
        this.immersion = new Immersion(() => this, {
          when: () => this.wide.matches && !this.headerPinned,
        });
        this.immersion.connect(arrival);
      },
      () => {},
    );
  }

  /**
   * The header is set to stay on screen. The section wrapper of the header group is what pins it
   * (sections/header.liquid); read from the computed style, so the header's own setting and the
   * homepage-only variant are both covered without this element knowing them.
   */
  get headerPinned() {
    return [...document.querySelectorAll('.shopify-section-group-header-group')].some(
      (group) => getComputedStyle(group).position === 'sticky',
    );
  }

  onWideChange = () => this.apply();

  /** Bring the section back to fill the screen (immersion.js decides whether the page moves). */
  reimmerse() {
    this.immersion?.immerse();
  }

  /**
   * A click in the section is a use of it, the tabs and the flip halves included. Not a use: a
   * click something else has already handled, a modified click (a new tab, say), the click that
   * ends selecting some copy, or a link in the copy, which takes the shopper elsewhere. The tabs
   * are links too, and their own listener prevents the click's default on a wide screen, so a
   * tab is a use before any of that is asked.
   */
  onUse = (event) => {
    if (event.target.closest('.process__tab')) {
      this.reimmerse();
      return;
    }
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.getSelection()?.toString()) return;
    if (event.target.closest('a')) return;
    this.reimmerse();
  };

  /** Tabs on a wide screen, a stack with jump links on a narrow one. */
  apply() {
    if (this.wide.matches) {
      this.dataset.scripted = '';
      this.tablist.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[i].id);
      });
      this.panels.forEach((panel, i) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[i].id);
        panel.tabIndex = 0;
      });
      this.select(this.index ?? 0);
      return;
    }

    // Stacked: the tabs are ordinary links to the sections below, and nothing is a tab or a
    // panel any more, so a screen reader is not told about a widget that is not there.
    delete this.dataset.scripted;
    this.tablist.removeAttribute('role');
    this.tabs.forEach((tab) => {
      tab.removeAttribute('role');
      tab.removeAttribute('aria-controls');
      tab.removeAttribute('aria-selected');
      tab.tabIndex = 0;
    });
    this.panels.forEach((panel) => {
      panel.hidden = false;
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
      panel.removeAttribute('tabindex');
    });
  }

  /**
   * One tab for one panel. A link rather than a button, so that before the script runs — and if it
   * never does — the same markup would still lead somewhere, and so the tab has an address.
   */
  buildTab(panel) {
    const tab = document.createElement('a');
    tab.className = 'process__tab';
    tab.id = `${panel.id}-tab`;
    tab.href = `#${panel.id}`;
    tab.textContent = panel.dataset.processLabel || '';
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', panel.id);
    tab.dataset.analyticsEvent = 'process_tab_select';
    panel.setAttribute('aria-labelledby', tab.id);
    this.tablist.append(tab);
    return tab;
  }

  onTabKeydown(event, i) {
    if (!this.wide.matches) return;
    const keys = {
      ArrowRight: i + 1,
      ArrowLeft: i - 1,
      Home: 0,
      End: this.tabs.length - 1,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    // the row wraps, which is what the tabs pattern expects
    const count = this.tabs.length;
    this.select((keys[event.key] + count) % count, { focusTab: true });
    this.reimmerse();
  }

  select(next, { focusTab = false } = {}) {
    const index = Math.min(Math.max(next, 0), this.tabs.length - 1);
    this.index = index;

    this.tabs.forEach((tab, i) => {
      const current = i === index;
      tab.setAttribute('aria-selected', current ? 'true' : 'false');
      // only the open tab is in the tab order; the arrow keys reach the rest
      tab.tabIndex = current ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.tabs.length - 1;
    if (focusTab) this.tabs[index].focus();

    this.dispatchEvent(
      new CustomEvent('process:select', {
        bubbles: true,
        detail: { index, label: this.panels[index].dataset.processLabel },
      }),
    );
  }
}

if (!customElements.get('process-tabs')) customElements.define('process-tabs', ProcessTabs);
